import React from "react";
import "./campaign-card.css";
import { calculateStreamsFromBudget } from "../../utils";
import { formatCampaignMoney, formatCampaignRatePer1k, normalizeCampaignCurrency } from "../../utils/campaignCurrency";

interface CuratorAllocation {
  _id: string;
  curatorIds: string[];           // Array of curator candidates
  winnerId?: string;              // The curator who accepted first (winner)
  rejectedByIds?: string[];       // Curators who rejected this allocation
  allocatedStreams: number;
  allocatedPercentage: number;
  allocatedBudget: number;
  offeredRatePer1k?: number;
  offeredPayout?: number;
  status: 'pending' | 'accepted' | 'rejected' | 'scheduled' | 'in-progress' | 'completed';
  response?: {
    playlistId?: string;
    playlistName?: string;
    playlistURL?: string;
    songPosition?: number;
    songLink?: string;
    confirmationLink?: string;
    respondedAt?: string;
    startTime?: string;
    endTime?: string;
    notes?: string;
    isScheduled?: boolean;
  };
  assignedAt: string;
  respondedAt?: string;
  completedAt?: string;
}

interface CampaignCardProps {
  _id: string;
  track_name: string;
  artist_name: string;
  spotify_link?: string;
  track_image: string;
  track_released?: boolean;
  track_lyrics?: any[];
  track_genre?: any[];
  campaign_type?: string;
  rejectedByIds: string[];
  acceptedById?: string;
  start_date?: string;
  end_date?: string;
  budget?: string;
  currencyCode?: string;
  status?: string;
  estimatedStreams?: number;
  offeredRatePer1k?: number;
  offeredPayout?: number;
  onAccept?: () => void;
  onReject?: () => void;
  currentUserId?: string;
  isRejecting?: boolean;
  curatorResponse?: {
    endTime?: string | number;
    [key: string]: any;
  };
  allocationMode?: 'single' | 'split';
  curatorAllocation?: CuratorAllocation;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  _id,
  track_name,
  artist_name,
  spotify_link,
  track_image,
  track_released = false,
  track_lyrics = [],
  track_genre = [],
  campaign_type = "one time",
  start_date,
  end_date = "--",
  budget,
  currencyCode,
  status = "",
  estimatedStreams,
  offeredRatePer1k,
  offeredPayout,
  onAccept = () => {},
  onReject = () => {},
  rejectedByIds = [],
  acceptedById = "",
  currentUserId,
  isRejecting = false,
  curatorResponse,
  allocationMode = 'single',
  curatorAllocation,
}) => {
  // Determine if this is a split campaign
  const isSplitMode = allocationMode === 'split';
  
  // For split mode, determine curator's state for this allocation
  const allocationStatus = curatorAllocation?.status;
  const isCandidate = curatorAllocation?.curatorIds?.includes(currentUserId || '');
  const isWinner = curatorAllocation?.winnerId === currentUserId;
  const isSuperseded = curatorAllocation?.winnerId && curatorAllocation.winnerId !== currentUserId;
  const hasRejected = curatorAllocation?.rejectedByIds?.includes(currentUserId || '');
  // Allocation states based on new multi-curator logic
  const isAllocationPending = allocationStatus === 'pending' && !curatorAllocation?.winnerId;
  const isAllocationAccepted = isWinner && allocationStatus && ['accepted', 'scheduled', 'in-progress', 'completed'].includes(allocationStatus);
  const isAllocationRejected = hasRejected || (allocationStatus === 'rejected' && !isWinner);
  const isAllocationCompleted = isWinner && allocationStatus === 'completed';
  const canAccept = isCandidate && !curatorAllocation?.winnerId && !hasRejected;
  
  // Check if the current user has already rejected this campaign (single mode)
  const isRejectedByCurrentUser =
    currentUserId && rejectedByIds.includes(currentUserId);

  // Check if the campaign is already accepted by someone else (single mode)
  const isAcceptedBySomeoneElse =
    acceptedById && acceptedById !== "" && acceptedById !== currentUserId;

  // Check if the current user is the one who accepted this campaign (single mode)
  const isAcceptedByCurrentUser =
    acceptedById && acceptedById === currentUserId;

  // Check if the campaign is successfully completed based on curatorResponse endTime
  const isSuccessfullyCompleted = () => {
    if (!curatorResponse || !curatorResponse.endTime) return false;

    const currentTime = new Date().getTime();
    let endTime: number;

    // Handle both string and number formats for endTime
    if (typeof curatorResponse.endTime === "string") {
      // If it's a string, try to parse it
      if (!isNaN(Number(curatorResponse.endTime))) {
        endTime = Number(curatorResponse.endTime);
      } else {
        endTime = new Date(curatorResponse.endTime).getTime();
      }
    } else {
      endTime = curatorResponse.endTime;
    }

    return currentTime > endTime;
  };

  // Campaign is completed only if current user accepted it AND the end time has passed
  const campaignCompleted =
    isAcceptedByCurrentUser && isSuccessfullyCompleted();

  // Format the dates for display
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "--") return "--";

    // If it's a timestamp
    if (!isNaN(Number(dateString))) {
      return new Date(Number(dateString)).toLocaleDateString();
    }

    // Otherwise try to parse as ISO date
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  // Format the lyrics for display
  const formatLyrics = (lyrics: any[]) => {
    if (!lyrics || lyrics.length === 0) return "--";

    // Return a span with badges for each language
    return (
      <div className="lyrics-badges">
        {lyrics.map((lyric, index) => (
          <span key={index} className="lyric-badge">
            {typeof lyric === "string" ? lyric : lyric.language || "Unknown"}
          </span>
        ))}
      </div>
    );
  };

  // Format the genre for display
  const formatGenre = (genre: any[]) => {
    if (!genre || genre.length === 0) return "--";

    return (
      <div className="lyrics-badges">
        {genre.map((g, index) => (
          <span key={index} className="lyric-badge genre-badge">
            {typeof g === "string" ? g : String(g)}
          </span>
        ))}
      </div>
    );
  };

  const budgetForCalculation = budget ? Number(budget) : 0;
  const normalizedCurrencyCode = normalizeCampaignCurrency(currencyCode);
  const toFiniteNumber = (value: unknown) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const roundCurrency = (value: unknown) =>
    Math.round((toFiniteNumber(value) + Number.EPSILON) * 100) / 100;

  const requestedStreams = Math.max(
    0,
    Math.round(
      isSplitMode && curatorAllocation
        ? toFiniteNumber(curatorAllocation.allocatedStreams)
        : toFiniteNumber(estimatedStreams) || calculateStreamsFromBudget(budgetForCalculation)
    )
  );

  const offeredPayoutInput = isSplitMode && curatorAllocation
    ? toFiniteNumber(curatorAllocation.offeredPayout || curatorAllocation.allocatedBudget)
    : toFiniteNumber(offeredPayout);
  const offeredRateInput = isSplitMode && curatorAllocation
    ? toFiniteNumber(curatorAllocation.offeredRatePer1k)
    : toFiniteNumber(offeredRatePer1k);
  const derivedRateFromPayout =
    requestedStreams > 0 && offeredPayoutInput > 0
      ? roundCurrency((offeredPayoutInput * 1000) / requestedStreams)
      : 0;
  const resolvedOfferedRatePer1k = roundCurrency(offeredRateInput > 0 ? offeredRateInput : derivedRateFromPayout || 6);
  const resolvedOfferedPayout = roundCurrency(
    offeredPayoutInput > 0
      ? offeredPayoutInput
      : (requestedStreams / 1000) * resolvedOfferedRatePer1k
  );
  const isCompetitiveRequest = isSplitMode && (curatorAllocation?.curatorIds?.length || 0) > 1;

  return (
    <div className="campaign-card">
      <div className="campaign-card-content">
        <div className="campaign-image-container">
          <a
            href={track_image || "https://dummyimage.com/300x300/000000/000000"}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={
                track_image || "https://dummyimage.com/300x300/000000/000000"
              }
              alt={`${track_name} cover art`}
              className="campaign-image"
            />
          </a>
        </div>
        
        <div className="campaign-details">
          <div className="campaign-header">
            <div>
              {!track_released && (
                <div className="unreleased-indicator">
                  <span className="unreleased-badge">UNRELEASED</span>
                  {start_date && (
                    <span className="release-date">
                      Release Date: {formatDate(start_date)}
                    </span>
                  )}
                  {status === 'scheduled' && curatorResponse && (
                    <div className="scheduled-info">
                      <span>Scheduled for: {new Date(curatorResponse.startTime).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
              
              <h3 className="campaign-title">{track_name}</h3>
              <p className="campaign-artist">{artist_name}</p>
            </div>
            {(requestedStreams > 0 || resolvedOfferedPayout > 0) && (
              <div className="campaign-card-status-top">
                <div className="campaign-card-budget">
                  <span>
                    Request:
                    <span className="budget-value">
                      {requestedStreams > 0 ? requestedStreams.toLocaleString() : 'N/A'} streams
                    </span>
                    <span className="budget-streams" style={{ paddingLeft: "6px"}}>
                      @ {formatCampaignRatePer1k(resolvedOfferedRatePer1k, normalizedCurrencyCode)} • {formatCampaignMoney(resolvedOfferedPayout, normalizedCurrencyCode)}
                    </span>
                  </span>
                </div>
                {isCompetitiveRequest && (
                  <span className="campaign-card-competition-note">First to accept wins</span>
                )}
              </div>
            )}
          </div>

          <div className="campaign-metadata">
            {spotify_link && (
              <div className="campaign-metadata-item">
                <svg
                  className="campaign-metadata-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                <a
                  href={spotify_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="campaign-link"
                >
                  {spotify_link}
                </a>
              </div>
            )}

            <div className="campaign-metadata-item">
              <svg
                className="campaign-metadata-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18V5l12-2v13"></path>
                <circle cx="6" cy="18" r="3"></circle>
                <circle cx="18" cy="16" r="3"></circle>
              </svg>
              <span>Released: {track_released ? "Yes" : "No"}</span>
            </div>

            <div className="campaign-metadata-item">
              <svg
                className="campaign-metadata-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span className="lyrics-badge-container">
                Lyrics: {formatLyrics(track_lyrics)}
              </span>
            </div>

            <div className="campaign-metadata-item">
              <svg
                className="campaign-metadata-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              <span className="lyrics-badge-container">
                Genre: {formatGenre(track_genre)}
              </span>
            </div>

            <div className="campaign-metadata-item">
              <svg
                className="campaign-metadata-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              <span>Type: {campaign_type}</span>
            </div>

            {start_date && (
              <div className="campaign-metadata-item">
                <svg
                  className="campaign-metadata-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span>Start: {formatDate(start_date)}</span>
              </div>
            )}

            {/* <div className="campaign-metadata-item">
              <svg
                className="campaign-metadata-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>End: {formatDate(end_date)}</span>
            </div> */}
          </div>
        </div>
      </div>

      <div className="campaign-footer">
        {isSplitMode ? (
          // Split mode footer - based on allocation status
          <>
            {isAllocationCompleted ? (
              <div className="campaign-completed-message">
                <svg
                  className="completed-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>Campaign Completed</span>
              </div>
            ) : isSuperseded ? (
              <div className="campaign-superseded-message">
                <svg
                  className="superseded-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>Another curator accepted this allocation first</span>
              </div>
            ) : isAllocationRejected ? (
              <div className="campaign-rejected-message">
                <svg
                  className="rejection-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>You have rejected this campaign</span>
              </div>
            ) : isAllocationAccepted ? (
              <div className="campaign-accepted-message">
                <svg
                  className="accepted-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>You won this allocation!</span>
              </div>
            ) : canAccept ? (
              <div className="campaign-actions">
                <div className="action-buttons">
                  <button
                    className="reject-button"
                    onClick={onReject}
                    disabled={isRejecting}
                  >
                    {isRejecting ? "Rejecting..." : "Reject"}
                  </button>
                  <button
                    className="accept-button"
                    onClick={onAccept}
                    disabled={isRejecting}
                  >
                    Add to Playlist
                  </button>
                </div>
              </div>
            ) : isAllocationPending ? (
              <div className="campaign-actions">
                <button
                  className="reject-button"
                  onClick={onReject}
                  disabled={isRejecting}
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </button>
                <button
                  className="accept-button"
                  onClick={onAccept}
                  disabled={isRejecting}
                >
                  Add to Playlist
                </button>
              </div>
            ) : null}
          </>
        ) : (
          // Single mode footer - existing logic
          <>
            {campaignCompleted ? (
              <div className="campaign-completed-message">
                <svg
                  className="completed-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>Successfully Completed</span>
              </div>
            ) : isRejectedByCurrentUser ? (
              <div className="campaign-rejected-message">
                <svg
                  className="rejection-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>You have rejected this campaign</span>
              </div>
            ) : isAcceptedByCurrentUser ? (
              <div className="campaign-accepted-message">
                <svg
                  className="accepted-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>You have already added this song to your playlist</span>
              </div>
            ) : isAcceptedBySomeoneElse ? (
              <div className="campaign-accepted-message">
                <svg
                  className="accepted-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>
                  Some other curator has added this song to their playlist
                </span>
              </div>
            ) : (
              <div className="campaign-actions">
                <button
                  className="reject-button"
                  onClick={onReject}
                  disabled={isRejecting}
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </button>
                <button
                  className="accept-button"
                  onClick={onAccept}
                  disabled={isRejecting}
                >
                  Add to Playlist
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignCard;
